/**
 * In-memory service bundles, enrollments, renewals and alert routing for the
 * mock API client (tracker item 85). Same rules as the backend where the UI
 * depends on them: bundle writes and enrollments are ADMIN / OFFICE_MANAGER
 * only (bundle reads: any staff; alert routing: ADMIN), one visit job per
 * component through the host, snapshotted steps, renewals that stay
 * PENDING_APPROVAL until approved (price from the renewal price, else the
 * price paid today, never the chart), RENEWAL_PRICE_CHANGED on approve, and
 * the renewal price prompt (RENEWAL_PRICE_CHOICE_REQUIRED).
 * Not modelled: work-day shifting (every day is a work day), tax (visit lines
 * come from the host), transactions, audit rows, customer messages.
 */
import { AxiosError, AxiosHeaders, AxiosResponse } from 'axios';
import * as types from './types';

function apiError(status: number, message: string, code?: string, details?: unknown): AxiosError {
  const config = { headers: new AxiosHeaders() };
  const response = {
    status, statusText: '', headers: {}, config,
    data: { success: false, message, ...(code ? { code } : {}), ...(details !== undefined ? { details } : {}) },
  } as AxiosResponse;
  return new AxiosError(message, undefined, config, undefined, response);
}

export interface MockBundleUser {
  id: string;
  tenantId: string;
  role: string;
  email: string;
  isActive: boolean;
  firstName?: string | null;
  lastName?: string | null;
}

export interface MockBundleItem {
  id: string;
  name: string;
  itemType: string;
  isActive?: boolean;
  deletedAt?: string | null;
}

export interface MockBundleProperty {
  id: string;
  customerId: string;
  address: string;
  city: string;
  state: string;
}

/** What the host knows about a visit job. */
export interface MockVisitJob {
  id: string;
  status: string;
  scheduledDate: string | null;
  completedDate: string | null;
  assignedTo: string | null;
  invoiceId: string | null;
  /** The first line's unit price, pre-tax amount and tax */
  unitPrice: string | null;
  lineTotal: string | null;
  lineTax: string | null;
}

export interface MockBundleHost {
  currentUser(): MockBundleUser;
  users(tenantId: string): MockBundleUser[];
  item(tenantId: string, itemId: string): MockBundleItem | null;
  property(tenantId: string, propertyId: string): MockBundleProperty | null;
  customer(tenantId: string, customerId: string): { id: string; name: string } | null;
  /** The unit price the item gets at the property (price chart, else catalog) */
  priceFor(tenantId: string, propertyId: string, itemId: string): { unitPrice: string; priceSource: string; taxRate: string };
  /** Create the ordinary visit job with its one line */
  createVisitJob(tenantId: string, input: {
    propertyId: string; assignedTo: string | null; scheduledDate: string; itemId: string; quantity: string; unitPrice: string;
    enrollment: { id: string; step: number; stepCount: number; bundleName: string };
  }): { id: string };
  job(tenantId: string, jobId: string): MockVisitJob | null;
  cancelJob(tenantId: string, jobId: string): void;
  setJobUnitPrice(tenantId: string, jobId: string, unitPrice: string): void;
  setJobDate(tenantId: string, jobId: string, date: string): void;
  /** Today in the company timezone (YYYY-MM-DD) */
  today(): string;
  /** The signed-in portal login (CUSTOMER_PORTAL, email confirmed); customerId is null while it is unlinked. Else 403. */
  portal(): { tenantId: string; userId: string; email: string; customerId: string | null };
  /** The customer's OWN properties (not ones only billed to them), oldest first */
  ownProperties(tenantId: string, customerId: string): MockBundleProperty[];
  /** Raise a staff bell alert to these users */
  alert?(tenantId: string, type: types.BundleAlertType, input: { title: string; body: string; enrollmentId: string; userIds: string[] }): void;
}

const OFFICE = ['ADMIN', 'OFFICE_MANAGER'];
const ALERT_TYPES: types.BundleAlertType[] = ['BUNDLE_RENEWAL_PENDING', 'BUNDLE_REQUEST_PENDING', 'BUNDLE_RENEWAL_BLOCKED'];
const DAY = 24 * 60 * 60 * 1000;
const OPEN_JOB_STATUSES = ['DRAFT', 'WORK_ORDER_GENERATED', 'SCHEDULED'];
const DONE_JOB_STATUSES = ['COMPLETED', 'INVOICED'];

const money = (n: number) => (Math.round(n * 100) / 100).toFixed(2);
const addDays = (date: string, days: number) => new Date(new Date(`${date}T00:00:00Z`).getTime() + days * DAY).toISOString().slice(0, 10);
const daysBetween = (from: string, to: string) => Math.round((new Date(`${to}T00:00:00Z`).getTime() - new Date(`${from}T00:00:00Z`).getTime()) / DAY);
const addYear = (date: string) => {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCFullYear(d.getUTCFullYear() + 1);
  return d.toISOString().slice(0, 10);
};
const validDate = (value: unknown): value is string => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`));

interface Bundle {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  isActive: boolean;
  portalRequestable: boolean;
  autoRenewDefault: boolean;
  deletedAt: string | null;
  components: Array<{ id: string; itemId: string; quantity: string; daysAfterPrevious: number; notes: string | null }>;
  createdAt: string;
  updatedAt: string;
}

interface Step {
  step: number;
  itemId: string;
  itemName: string;
  quantity: string;
  daysAfterPrevious: number;
  plannedDate: string;
  unitPrice: string;
  renewalUnitPrice: string | null;
  jobId: string | null;
}

interface Enrollment {
  id: string;
  tenantId: string;
  bundleId: string | null;
  bundleName: string;
  customerId: string;
  propertyId: string;
  startDate: string;
  status: types.EnrollmentStatus;
  autoRenew: boolean;
  renewedFromId: string | null;
  renewedToId: string | null;
  createdBy: string;
  cancelledAt: string | null;
  cancelReason: string | null;
  steps: Step[];
  createdAt: string;
  updatedAt: string;
}

/** A bundle as an estimate sees it (see bundleTemplate). */
export interface MockBundleTemplate {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  autoRenewDefault: boolean;
  components: Array<{
    id: string; sortOrder: number; itemId: string; itemName: string; quantity: string; offsetDays: number; daysAfterPrevious: number;
    notes: string | null; available: boolean; reason: 'ITEM_DELETED' | 'ITEM_INACTIVE' | null;
  }>;
}

export class MockBundles {
  private bundles: Bundle[] = [];
  private enrollments: Enrollment[] = [];
  private routing = new Map<string, types.BundleAlertRouting>();
  private seq = 0;

  constructor(private readonly host: MockBundleHost) {}

  private id(prefix: string): string {
    this.seq += 1;
    return `${prefix}-${Date.now().toString(36)}-${this.seq}`;
  }

  private user(): MockBundleUser {
    return this.host.currentUser();
  }

  private requireOffice(): MockBundleUser {
    const user = this.user();
    if (!OFFICE.includes(user.role)) throw apiError(403, 'Insufficient permissions', 'INSUFFICIENT_PERMISSIONS');
    return user;
  }

  private nowIso(): string {
    return new Date().toISOString();
  }

  // ------------------------------------------------------------ bundles

  private presentBundle(bundle: Bundle): types.ServiceBundle {
    const tenantId = bundle.tenantId;
    const components = bundle.components.map((c, index) => {
      const item = this.host.item(tenantId, c.itemId);
      return {
        id: c.id, sortOrder: index + 1, itemId: c.itemId, itemName: item?.name ?? 'Deleted item', itemType: item?.itemType ?? 'SERVICE',
        itemAvailable: !!item && !item.deletedAt, quantity: c.quantity, daysAfterPrevious: c.daysAfterPrevious, notes: c.notes,
      };
    });
    return {
      id: bundle.id, name: bundle.name, description: bundle.description, isActive: bundle.isActive, portalRequestable: bundle.portalRequestable,
      autoRenewDefault: bundle.autoRenewDefault, components, componentCount: components.length,
      visitSpanDays: components.slice(1).reduce((sum, c) => sum + c.daysAfterPrevious, 0), createdAt: bundle.createdAt, updatedAt: bundle.updatedAt,
    };
  }

  private liveBundle(tenantId: string, id: string): Bundle {
    const bundle = this.bundles.find((b) => b.id === id && b.tenantId === tenantId && !b.deletedAt);
    if (!bundle) throw apiError(404, 'Service bundle not found', 'NOT_FOUND');
    return bundle;
  }

  private validComponents(tenantId: string, raw: unknown): Bundle['components'] {
    if (!Array.isArray(raw) || raw.length === 0) throw apiError(400, 'components: At least one component is required', 'VALIDATION_ERROR');
    return raw.map((c: types.BundleComponentInput) => {
      const item = c?.itemId ? this.host.item(tenantId, c.itemId) : null;
      if (!item || item.deletedAt) throw apiError(404, 'Item not found', 'NOT_FOUND');
      const quantity = Number(c.quantity);
      if (!Number.isFinite(quantity) || quantity <= 0) throw apiError(400, 'quantity: Must be greater than 0', 'VALIDATION_ERROR');
      const gap = c.daysAfterPrevious;
      if (!Number.isInteger(gap) || gap < 0 || gap > 3650) throw apiError(400, 'daysAfterPrevious: Must be a whole number from 0 to 3650', 'VALIDATION_ERROR');
      return { id: this.id('bc'), itemId: item.id, quantity: String(c.quantity), daysAfterPrevious: gap, notes: c.notes?.trim() || null };
    });
  }

  private assertUniqueName(tenantId: string, name: string, exceptId?: string): void {
    if (this.bundles.some((b) => b.tenantId === tenantId && !b.deletedAt && b.id !== exceptId && b.name.toLowerCase() === name.toLowerCase())) {
      throw apiError(409, 'A service bundle with this name already exists', 'DUPLICATE_NAME');
    }
  }

  listBundles(filters: types.ServiceBundleListFilters = {}): types.ServiceBundle[] {
    const user = this.user();
    const term = filters.search?.trim().toLowerCase();
    return this.bundles
      .filter((b) => b.tenantId === user.tenantId && !b.deletedAt)
      .filter((b) => filters.isActive === undefined || b.isActive === filters.isActive)
      .filter((b) => filters.portalRequestable === undefined || b.portalRequestable === filters.portalRequestable)
      .filter((b) => !term || b.name.toLowerCase().includes(term))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((b) => this.presentBundle(b));
  }

  getBundle(id: string): types.ServiceBundle {
    return this.presentBundle(this.liveBundle(this.user().tenantId, id));
  }

  createBundle(data: types.CreateServiceBundleRequest): types.ServiceBundle {
    const user = this.requireOffice();
    const name = data.name?.trim() ?? '';
    if (!name) throw apiError(400, 'name: Required', 'VALIDATION_ERROR');
    if (name.length > 255) throw apiError(400, 'name: At most 255 characters', 'VALIDATION_ERROR');
    const components = this.validComponents(user.tenantId, data.components);
    this.assertUniqueName(user.tenantId, name);
    const now = this.nowIso();
    const bundle: Bundle = {
      id: this.id('bundle'), tenantId: user.tenantId, name, description: data.description?.trim() || null, isActive: data.isActive ?? true,
      portalRequestable: data.portalRequestable ?? false, autoRenewDefault: data.autoRenewDefault ?? false, deletedAt: null, components, createdAt: now, updatedAt: now,
    };
    this.bundles.push(bundle);
    return this.presentBundle(bundle);
  }

  updateBundle(id: string, data: types.UpdateServiceBundleRequest): types.ServiceBundle {
    const user = this.requireOffice();
    const bundle = this.liveBundle(user.tenantId, id);
    const components = this.validComponents(user.tenantId, data.components);
    if (data.name !== undefined) {
      const name = data.name.trim();
      if (!name) throw apiError(400, 'name: Required', 'VALIDATION_ERROR');
      this.assertUniqueName(user.tenantId, name, id);
      bundle.name = name;
    }
    if (data.description !== undefined) bundle.description = data.description?.trim() || null;
    if (data.isActive !== undefined) bundle.isActive = data.isActive;
    if (data.portalRequestable !== undefined) bundle.portalRequestable = data.portalRequestable;
    // Changing the default never changes an existing enrollment
    if (data.autoRenewDefault !== undefined) bundle.autoRenewDefault = data.autoRenewDefault;
    bundle.components = components;
    bundle.updatedAt = this.nowIso();
    return this.presentBundle(bundle);
  }

  deleteBundle(id: string): void {
    const user = this.requireOffice();
    this.liveBundle(user.tenantId, id).deletedAt = this.nowIso();
  }

  /** Planned dates: the first visit on the start date, then the gaps from the nominal dates. */
  private planDates(startDate: string, gaps: number[]): string[] {
    let nominal = startDate;
    return gaps.map((gap, index) => {
      if (index > 0) nominal = addDays(nominal, gap);
      return nominal;
    });
  }

  previewBundle(id: string, data: { propertyId: string; startDate: string }): types.BundlePreview {
    const user = this.requireOffice();
    const bundle = this.liveBundle(user.tenantId, id);
    return this.plan(user.tenantId, bundle, data.propertyId, data.startDate).preview;
  }

  private plan(tenantId: string, bundle: Bundle, propertyId: string, startDate: string) {
    if (!validDate(startDate)) throw apiError(400, 'startDate: Must be a date (YYYY-MM-DD)', 'VALIDATION_ERROR');
    const property = this.host.property(tenantId, propertyId);
    if (!property) throw apiError(404, 'Property not found', 'NOT_FOUND');
    const usable: Bundle['components'] = [];
    const skipped: types.BundleSkippedComponent[] = [];
    bundle.components.forEach((c, index) => {
      const item = this.host.item(tenantId, c.itemId);
      if (!item || item.deletedAt) skipped.push({ componentId: c.id, sortOrder: index + 1, itemId: c.itemId, itemName: item?.name ?? 'Deleted item', reason: 'ITEM_DELETED' });
      else if (item.isActive === false) skipped.push({ componentId: c.id, sortOrder: index + 1, itemId: c.itemId, itemName: item.name, reason: 'ITEM_INACTIVE' });
      else usable.push(c);
    });
    if (usable.length === 0) throw apiError(400, 'None of the items in this bundle can be scheduled', 'BUNDLE_EMPTY');
    const dates = this.planDates(startDate, usable.map((c) => c.daysAfterPrevious));
    let subtotal = 0;
    let totalTax = 0;
    const visits = usable.map((c, index): types.BundlePreviewVisit => {
      const item = this.host.item(tenantId, c.itemId)!;
      const priced = this.host.priceFor(tenantId, propertyId, c.itemId);
      const lineTotal = Number(c.quantity) * Number(priced.unitPrice);
      const lineTax = lineTotal * Number(priced.taxRate);
      subtotal += lineTotal;
      totalTax += lineTax;
      return {
        step: index + 1, componentId: c.id, itemId: c.itemId, itemName: item.name, quantity: c.quantity, nominalDate: dates[index], plannedDate: dates[index],
        daysAfterPrevious: index === 0 ? 0 : c.daysAfterPrevious, unitPrice: money(Number(priced.unitPrice)), priceSource: priced.priceSource, taxRate: priced.taxRate,
        lineTotal: money(lineTotal), lineTax: money(lineTax), lineAmount: money(lineTotal + lineTax), notes: c.notes,
      };
    });
    const preview: types.BundlePreview = {
      bundleId: bundle.id, bundleName: bundle.name, propertyId, startDate, stepCount: visits.length, visits, skipped,
      subtotal: money(subtotal), totalTax: money(totalTax), total: money(subtotal + totalTax),
    };
    return { preview, property };
  }

  // ------------------------------------------------------------ enrollments

  private liveEnrollment(tenantId: string, id: string): Enrollment {
    const enrollment = this.enrollments.find((e) => e.id === id && e.tenantId === tenantId);
    if (!enrollment) throw apiError(404, 'Enrollment not found', 'NOT_FOUND');
    return enrollment;
  }

  /** A step is finished when its job is completed / invoiced, or cancelled. */
  private stepState(enrollment: Enrollment, step: Step): 'DONE' | 'CANCELLED' | 'OPEN' {
    const job = step.jobId ? this.host.job(enrollment.tenantId, step.jobId) : null;
    if (job && DONE_JOB_STATUSES.includes(job.status)) return 'DONE';
    if (job?.status === 'CANCELLED') return 'CANCELLED';
    if (enrollment.status === 'CANCELLED' && !job) return 'CANCELLED';
    return 'OPEN';
  }

  /** ACTIVE becomes COMPLETED once every visit is completed or cancelled (and auto-renew may then queue a renewal). */
  private syncStatus(enrollment: Enrollment): void {
    if (enrollment.status !== 'ACTIVE') return;
    if (enrollment.steps.every((s) => this.stepState(enrollment, s) !== 'OPEN')) {
      enrollment.status = 'COMPLETED';
      enrollment.updatedAt = this.nowIso();
      if (enrollment.autoRenew && !enrollment.renewedToId && enrollment.steps.some((s) => this.stepState(enrollment, s) === 'DONE')) {
        this.tryAutoRenew(enrollment);
      }
    }
  }

  /** A visit moved by `days`: the later visits that are still scheduled move by the same number (started or finished ones never do). */
  shiftLaterVisits(tenantId: string, jobId: string, days: number): void {
    const enrollment = this.enrollments.find((e) => e.tenantId === tenantId && e.steps.some((s) => s.jobId === jobId));
    const from = enrollment?.steps.find((s) => s.jobId === jobId);
    if (!enrollment || !from || days === 0) return;
    enrollment.steps.filter((s) => s.step > from.step && s.jobId).forEach((s) => {
      const job = this.host.job(tenantId, s.jobId!);
      if (job && job.status === 'SCHEDULED' && job.scheduledDate) this.host.setJobDate(tenantId, job.id, addDays(job.scheduledDate, days));
    });
  }

  /** Called by the host after a visit job changed status. */
  jobChanged(tenantId: string, jobId: string): void {
    const enrollment = this.enrollments.find((e) => e.tenantId === tenantId && e.steps.some((s) => s.jobId === jobId));
    if (enrollment) this.syncStatus(enrollment);
  }

  private tryAutoRenew(enrollment: Enrollment): void {
    const bundle = enrollment.bundleId ? this.bundles.find((b) => b.id === enrollment.bundleId && !b.deletedAt) : null;
    if (!bundle || !bundle.isActive) {
      this.alert(enrollment, 'BUNDLE_RENEWAL_BLOCKED', 'Automatic renewal blocked', `The ${enrollment.bundleName} could not be renewed automatically: the bundle is ${bundle ? 'inactive' : 'deleted'}.`);
      return;
    }
    this.createPendingRenewal(enrollment, undefined, enrollment.createdBy);
  }

  private presentEnrollment(enrollment: Enrollment): types.BundleEnrollment {
    this.syncStatus(enrollment);
    const tenantId = enrollment.tenantId;
    const customer = this.host.customer(tenantId, enrollment.customerId);
    const property = this.host.property(tenantId, enrollment.propertyId);
    let completed = 0;
    let cancelled = 0;
    let total = 0;
    let next: string | null = null;
    const steps = enrollment.steps.map((step): types.EnrollmentStep => {
      const job = step.jobId ? this.host.job(tenantId, step.jobId) : null;
      const state = this.stepState(enrollment, step);
      if (state === 'DONE') completed += 1;
      if (state === 'CANCELLED') cancelled += 1;
      const lineTotal = job?.lineTotal ?? money(Number(step.quantity) * Number(step.unitPrice));
      if (state !== 'CANCELLED') total += Number(lineTotal);
      if (job && OPEN_JOB_STATUSES.includes(job.status) && job.scheduledDate && (!next || job.scheduledDate < next)) next = job.scheduledDate;
      const effective = this.effectivePrice(enrollment, step);
      return {
        step: step.step, itemId: step.itemId, itemName: step.itemName, quantity: step.quantity, daysAfterPrevious: step.daysAfterPrevious,
        plannedDate: step.plannedDate, unitPrice: step.unitPrice, renewalUnitPrice: step.renewalUnitPrice, effectiveRenewalPrice: effective.price,
        renewalPriceSource: effective.source, jobUnitPrice: job?.unitPrice ?? null, lineTotal, lineTax: job?.lineTax ?? null, jobId: step.jobId,
        job: job ? { id: job.id, status: job.status, scheduledDate: job.scheduledDate, completedDate: job.completedDate, assignedTo: job.assignedTo, invoiceId: job.invoiceId } : null,
      };
    });
    const endDate = steps.length ? steps.map((s) => s.job?.scheduledDate ?? s.plannedDate).sort().slice(-1)[0] : enrollment.startDate;
    return {
      id: enrollment.id, bundleId: enrollment.bundleId, bundleName: enrollment.bundleName, customerId: enrollment.customerId,
      customer: { id: enrollment.customerId, name: customer?.name ?? '' }, propertyId: enrollment.propertyId,
      property: { id: enrollment.propertyId, address: property?.address ?? '', city: property?.city ?? '', state: property?.state ?? '' },
      startDate: enrollment.startDate, endDate, status: enrollment.status, stepCount: enrollment.steps.length, autoRenew: enrollment.autoRenew,
      renewedFromId: enrollment.renewedFromId, renewedToId: enrollment.renewedToId, createdBy: enrollment.createdBy, cancelledAt: enrollment.cancelledAt,
      cancelReason: enrollment.cancelReason,
      progress: { total: steps.length, completed, cancelled, remaining: steps.length - completed - cancelled },
      nextVisitDate: next, total: money(total), steps, createdAt: enrollment.createdAt, updatedAt: enrollment.updatedAt,
    };
  }

  /** Decision 13: the renewal price, else what the customer pays today, else the step's own price. Never the chart. */
  private effectivePrice(enrollment: Enrollment, step: Step): { price: string; source: types.RenewalPriceSource } {
    if (step.renewalUnitPrice !== null) return { price: step.renewalUnitPrice, source: 'RENEWAL_PRICE' };
    const job = step.jobId ? this.host.job(enrollment.tenantId, step.jobId) : null;
    if (job && job.status !== 'CANCELLED' && job.unitPrice !== null) return { price: job.unitPrice, source: 'LAST_CHARGED' };
    return { price: step.unitPrice, source: 'ORIGINAL' };
  }

  private paged<T>(rows: T[], page = 1, limit = 20) {
    const pages = Math.max(1, Math.ceil(rows.length / limit));
    return { data: rows.slice((page - 1) * limit, page * limit), pagination: { page, limit, total: rows.length, pages } };
  }

  listEnrollments(filters: types.EnrollmentListFilters = {}): types.EnrollmentPage {
    const user = this.requireOffice();
    const rows = this.enrollments
      .filter((e) => e.tenantId === user.tenantId)
      .filter((e) => !filters.customerId || e.customerId === filters.customerId)
      .filter((e) => !filters.propertyId || e.propertyId === filters.propertyId)
      .filter((e) => !filters.bundleId || e.bundleId === filters.bundleId)
      .filter((e) => filters.autoRenew === undefined || e.autoRenew === filters.autoRenew)
      .map((e) => this.presentEnrollment(e))
      .filter((e) => !filters.status || e.status === filters.status)
      .sort((a, b) => b.startDate.localeCompare(a.startDate));
    return this.paged(rows, filters.page, filters.limit);
  }

  listPropertyEnrollments(propertyId: string, filters: types.EnrollmentListFilters = {}): types.EnrollmentPage {
    return this.listEnrollments({ ...filters, propertyId });
  }

  getEnrollment(id: string): types.BundleEnrollment {
    const user = this.requireOffice();
    return this.presentEnrollment(this.liveEnrollment(user.tenantId, id));
  }

  addBundleToProperty(propertyId: string, data: types.AddBundleRequest): types.CreatedEnrollment {
    const user = this.requireOffice();
    if (!data.bundleId) throw apiError(400, 'bundleId: Required', 'VALIDATION_ERROR');
    const bundle = this.liveBundle(user.tenantId, data.bundleId);
    const { preview, property } = this.plan(user.tenantId, bundle, propertyId, data.startDate);
    const now = this.nowIso();
    const enrollment: Enrollment = {
      id: this.id('enr'), tenantId: user.tenantId, bundleId: bundle.id, bundleName: bundle.name, customerId: property.customerId, propertyId,
      startDate: data.startDate, status: 'ACTIVE', autoRenew: data.autoRenew ?? bundle.autoRenewDefault, renewedFromId: null, renewedToId: null,
      createdBy: user.id, cancelledAt: null, cancelReason: null, steps: [], createdAt: now, updatedAt: now,
    };
    enrollment.steps = preview.visits.map((v): Step => ({
      step: v.step, itemId: v.itemId, itemName: v.itemName, quantity: v.quantity, daysAfterPrevious: v.daysAfterPrevious, plannedDate: v.plannedDate,
      unitPrice: v.unitPrice, renewalUnitPrice: null, jobId: null,
    }));
    this.createVisitJobs(enrollment, data.assignedTo ?? null);
    this.enrollments.push(enrollment);
    return { ...this.presentEnrollment(enrollment), skipped: preview.skipped };
  }

  private createVisitJobs(enrollment: Enrollment, assignedTo: string | null): void {
    enrollment.steps.forEach((step) => {
      const job = this.host.createVisitJob(enrollment.tenantId, {
        propertyId: enrollment.propertyId, assignedTo, scheduledDate: step.plannedDate, itemId: step.itemId, quantity: step.quantity, unitPrice: step.unitPrice,
        enrollment: { id: enrollment.id, step: step.step, stepCount: enrollment.steps.length, bundleName: enrollment.bundleName },
      });
      step.jobId = job.id;
    });
  }

  updateAutoRenew(id: string, autoRenew: boolean): types.BundleEnrollment {
    const user = this.requireOffice();
    const enrollment = this.liveEnrollment(user.tenantId, id);
    if (typeof autoRenew !== 'boolean') throw apiError(400, 'autoRenew: Must be true or false', 'VALIDATION_ERROR');
    if (enrollment.status === 'CANCELLED') throw apiError(409, 'A cancelled enrollment cannot be changed', 'ENROLLMENT_CANCELLED');
    enrollment.autoRenew = autoRenew;
    enrollment.updatedAt = this.nowIso();
    return this.presentEnrollment(enrollment);
  }

  cancelEnrollment(id: string, reason?: string): types.BundleEnrollment {
    const user = this.requireOffice();
    const enrollment = this.liveEnrollment(user.tenantId, id);
    if (enrollment.status === 'CANCELLED') throw apiError(409, 'This enrollment is already cancelled', 'ENROLLMENT_CANCELLED');
    if (enrollment.status === 'PENDING_APPROVAL') throw apiError(409, 'Decline a pending enrollment instead', 'ENROLLMENT_PENDING');
    enrollment.steps.forEach((step) => {
      const job = step.jobId ? this.host.job(enrollment.tenantId, step.jobId) : null;
      if (job && OPEN_JOB_STATUSES.includes(job.status)) this.host.cancelJob(enrollment.tenantId, job.id);
    });
    enrollment.status = 'CANCELLED';
    enrollment.cancelledAt = this.nowIso();
    enrollment.cancelReason = reason?.trim() || null;
    enrollment.updatedAt = enrollment.cancelledAt;
    return this.presentEnrollment(enrollment);
  }

  // ------------------------------------------------------------ renewal price prompt

  /**
   * The line price of a bundle visit job is changing (JobLine edit): throws
   * RENEWAL_PRICE_CHOICE_REQUIRED without a choice, otherwise applies it.
   * Called by the host inside updateJobLineItem before it saves anything.
   */
  visitPriceChanging(tenantId: string, jobId: string, newUnitPrice: string, action: types.RenewalPriceAction | undefined): void {
    const enrollment = this.enrollments.find((e) => e.tenantId === tenantId && e.steps.some((s) => s.jobId === jobId));
    if (!enrollment) return;
    const step = enrollment.steps.find((s) => s.jobId === jobId)!;
    const job = this.host.job(tenantId, jobId);
    const oldPrice = job?.unitPrice ?? step.unitPrice;
    if (Number(oldPrice) === Number(newUnitPrice)) return;
    if (action !== undefined && action !== 'UPDATE' && action !== 'KEEP') throw apiError(400, "renewalPriceAction must be 'UPDATE' or 'KEEP'", 'VALIDATION_ERROR');
    if (!action) {
      const details: types.RenewalPriceChoiceDetails = {
        enrollmentId: enrollment.id, step: step.step, jobId, oldUnitPrice: money(Number(oldPrice)), newUnitPrice: money(Number(newUnitPrice)),
        renewalUnitPrice: step.renewalUnitPrice, currentRenewalPrice: step.renewalUnitPrice ?? money(Number(oldPrice)),
      };
      throw apiError(400, 'Choose whether to also update the renewal price', 'RENEWAL_PRICE_CHOICE_REQUIRED', details);
    }
    const after = action === 'UPDATE' ? money(Number(newUnitPrice)) : step.renewalUnitPrice ?? money(Number(oldPrice));
    step.renewalUnitPrice = after;
    // A pending renewal's matching step follows the resulting renewal price
    const pending = enrollment.renewedToId ? this.enrollments.find((e) => e.id === enrollment.renewedToId && e.status === 'PENDING_APPROVAL') : null;
    const match = pending?.steps.find((s) => s.step === step.step);
    if (match) match.unitPrice = after;
  }

  updateStepPrice(id: string, stepNumber: number, data: types.StepPriceRequest): types.BundleEnrollment {
    const user = this.requireOffice();
    const enrollment = this.liveEnrollment(user.tenantId, id);
    const step = enrollment.steps.find((s) => s.step === Number(stepNumber));
    if (!step) throw apiError(404, 'Visit not found', 'NOT_FOUND');
    const price = Number(data.unitPrice);
    if (!Number.isFinite(price) || price < 0) throw apiError(400, 'unitPrice: Must be a number of 0 or more', 'VALIDATION_ERROR');
    if (!step.jobId) throw apiError(409, 'This visit has no job yet, so its price cannot be edited here', 'ENROLLMENT_STEP_NOT_EDITABLE');
    this.visitPriceChanging(user.tenantId, step.jobId, String(price), data.renewalPriceAction);
    this.host.setJobUnitPrice(user.tenantId, step.jobId, money(price));
    enrollment.updatedAt = this.nowIso();
    return this.presentEnrollment(enrollment);
  }

  // ------------------------------------------------------------ renewals

  private quoteSteps(steps: Array<{ step: number; itemId: string; itemName: string; quantity: string; daysAfterPrevious: number; plannedDate: string; price: string; source: types.RenewalPriceSource; current: string | null; renewal: string | null }>, tenantId: string, propertyId: string) {
    let subtotal = 0;
    let tax = 0;
    const rows = steps.map((s): types.RenewalQuoteStep => {
      const rate = Number(this.host.priceFor(tenantId, propertyId, s.itemId).taxRate);
      const lineTotal = Number(s.quantity) * Number(s.price);
      const lineTax = lineTotal * rate;
      subtotal += lineTotal;
      tax += lineTax;
      return {
        step: s.step, itemId: s.itemId, itemName: s.itemName, quantity: s.quantity, daysAfterPrevious: s.daysAfterPrevious, plannedDate: s.plannedDate,
        unitPrice: money(Number(s.price)), priceSource: s.source, currentUnitPrice: s.current, renewalUnitPrice: s.renewal, taxRate: String(rate),
        lineTotal: money(lineTotal), lineTax: money(lineTax), lineAmount: money(lineTotal + lineTax),
      };
    });
    return { rows, subtotal: money(subtotal), tax: money(tax), total: money(subtotal + tax) };
  }

  getQuote(id: string, startDate?: string): types.RenewalQuote {
    const user = this.requireOffice();
    const enrollment = this.liveEnrollment(user.tenantId, id);
    const property = this.host.property(user.tenantId, enrollment.propertyId);
    const customer = this.host.customer(user.tenantId, enrollment.customerId);
    if (startDate !== undefined && !validDate(startDate)) throw apiError(400, 'startDate: Must be a date (YYYY-MM-DD)', 'VALIDATION_ERROR');
    const base = {
      enrollmentId: enrollment.id, bundleId: enrollment.bundleId, bundleName: enrollment.bundleName, customer: { id: enrollment.customerId, name: customer?.name ?? '' },
      property: { id: enrollment.propertyId, address: property?.address ?? '', city: property?.city ?? '', state: property?.state ?? '' },
      autoRenew: enrollment.autoRenew, skipped: [] as types.RenewalQuote['skipped'],
    };
    if (enrollment.status === 'PENDING_APPROVAL') {
      const source = enrollment.renewedFromId ? this.enrollments.find((e) => e.id === enrollment.renewedFromId) : null;
      const steps = enrollment.steps.map((s) => ({
        step: s.step, itemId: s.itemId, itemName: s.itemName, quantity: s.quantity, daysAfterPrevious: s.daysAfterPrevious, plannedDate: s.plannedDate,
        price: s.unitPrice, source: 'RENEWAL_PRICE' as types.RenewalPriceSource, current: source?.steps.find((x) => x.step === s.step) ? this.currentPrice(source, s.step) : null,
        renewal: source?.steps.find((x) => x.step === s.step)?.renewalUnitPrice ?? null,
      }));
      const q = this.quoteSteps(steps, user.tenantId, enrollment.propertyId);
      return { ...base, kind: source ? 'PENDING_RENEWAL' : 'PENDING_REQUEST', startDate: enrollment.startDate, renewal: null, steps: q.rows, subtotal: q.subtotal, estimatedTax: q.tax, estimatedTotal: q.total };
    }
    if (enrollment.status === 'CANCELLED') throw apiError(409, 'A cancelled enrollment cannot be renewed', 'ENROLLMENT_NOT_RENEWABLE');
    const start = startDate ?? addYear(enrollment.startDate);
    const dates = this.planDates(start, enrollment.steps.map((s) => s.daysAfterPrevious));
    const steps = enrollment.steps.map((s, index) => {
      const eff = this.effectivePrice(enrollment, s);
      return {
        step: s.step, itemId: s.itemId, itemName: s.itemName, quantity: s.quantity, daysAfterPrevious: s.daysAfterPrevious, plannedDate: dates[index],
        price: eff.price, source: eff.source, current: this.currentPrice(enrollment, s.step), renewal: s.renewalUnitPrice,
      };
    });
    const q = this.quoteSteps(steps, user.tenantId, enrollment.propertyId);
    const existing = enrollment.renewedToId ? this.enrollments.find((e) => e.id === enrollment.renewedToId) : null;
    return { ...base, kind: 'RENEWAL_PREVIEW', startDate: start, renewal: existing ? { id: existing.id, status: existing.status } : null, steps: q.rows, subtotal: q.subtotal, estimatedTax: q.tax, estimatedTotal: q.total };
  }

  private currentPrice(enrollment: Enrollment, stepNumber: number): string | null {
    const step = enrollment.steps.find((s) => s.step === stepNumber);
    const job = step?.jobId ? this.host.job(enrollment.tenantId, step.jobId) : null;
    return job?.unitPrice ?? null;
  }

  createRenewal(id: string, startDate?: string): types.RenewalCreateResult {
    const user = this.requireOffice();
    const enrollment = this.liveEnrollment(user.tenantId, id);
    if (startDate !== undefined && !validDate(startDate)) throw apiError(400, 'startDate: Must be a date (YYYY-MM-DD)', 'VALIDATION_ERROR');
    const existing = enrollment.renewedToId ? this.enrollments.find((e) => e.id === enrollment.renewedToId) : null;
    if (existing) return { created: false, renewal: this.presentEnrollment(existing) };
    if (!['ACTIVE', 'COMPLETED'].includes(enrollment.status)) throw apiError(409, 'Only an active or completed enrollment can be renewed', 'ENROLLMENT_NOT_RENEWABLE');
    const renewal = this.createPendingRenewal(enrollment, startDate, user.id);
    return { created: true, renewal: this.presentEnrollment(renewal) };
  }

  private createPendingRenewal(source: Enrollment, startDate: string | undefined, actorId: string): Enrollment {
    const start = startDate ?? addYear(source.startDate);
    const dates = this.planDates(start, source.steps.map((s) => s.daysAfterPrevious));
    const now = this.nowIso();
    const renewal: Enrollment = {
      id: this.id('enr'), tenantId: source.tenantId, bundleId: source.bundleId, bundleName: source.bundleName, customerId: source.customerId, propertyId: source.propertyId,
      startDate: start, status: 'PENDING_APPROVAL', autoRenew: source.autoRenew, renewedFromId: source.id, renewedToId: null, createdBy: actorId,
      cancelledAt: null, cancelReason: null, createdAt: now, updatedAt: now,
      steps: source.steps.map((s, index): Step => ({
        step: s.step, itemId: s.itemId, itemName: s.itemName, quantity: s.quantity, daysAfterPrevious: s.daysAfterPrevious, plannedDate: dates[index],
        unitPrice: money(Number(this.effectivePrice(source, s).price)), renewalUnitPrice: null, jobId: null,
      })),
    };
    this.enrollments.push(renewal);
    source.renewedToId = renewal.id;
    this.alert(renewal, 'BUNDLE_RENEWAL_PENDING', 'Bundle renewal awaiting approval', `${source.bundleName} was renewed for approval.`, source.createdBy);
    return renewal;
  }

  createRenewalsBulk(enrollmentIds: string[], startDate?: string): types.BulkRenewalResult {
    this.requireOffice();
    if (!Array.isArray(enrollmentIds) || enrollmentIds.length === 0) throw apiError(400, 'enrollmentIds: Choose at least one enrollment', 'VALIDATION_ERROR');
    if (enrollmentIds.length > 200) throw apiError(400, 'enrollmentIds: At most 200 per request', 'VALIDATION_ERROR');
    const results = enrollmentIds.map((enrollmentId): types.BulkRenewalRowResult => {
      try {
        const result = this.createRenewal(enrollmentId, startDate);
        return { enrollmentId, success: true, created: result.created, renewalId: result.renewal.id };
      } catch (err) {
        const data = (err as AxiosError<{ message?: string; code?: string }>).response?.data;
        return { enrollmentId, success: false, error: { code: data?.code ?? 'ERROR', message: data?.message ?? 'Could not create the renewal' } };
      }
    });
    const succeeded = results.filter((r) => r.success).length;
    return { total: results.length, succeeded, failed: results.length - succeeded, results };
  }

  private renewalRows(filters: types.RenewalListFilters): types.RenewalRow[] {
    const user = this.requireOffice();
    const today = this.host.today();
    return this.enrollments
      .filter((e) => e.tenantId === user.tenantId && ['ACTIVE', 'COMPLETED'].includes(e.status))
      .filter((e) => !filters.customerId || e.customerId === filters.customerId)
      .filter((e) => !filters.propertyId || e.propertyId === filters.propertyId)
      .filter((e) => !filters.bundleId || e.bundleId === filters.bundleId)
      .filter((e) => filters.autoRenew === undefined || e.autoRenew === filters.autoRenew)
      .filter((e) => !filters.status || e.status === filters.status)
      .filter((e) => !filters.year || Number(e.startDate.slice(0, 4)) === filters.year)
      .map((e): types.RenewalRow => {
        const view = this.presentEnrollment(e);
        const renewal = e.renewedToId ? this.enrollments.find((x) => x.id === e.renewedToId) : null;
        const quoteSteps = e.steps.map((s) => Number(s.quantity) * Number(this.effectivePrice(e, s).price));
        return {
          id: e.id, bundleId: e.bundleId, bundleName: e.bundleName, customer: view.customer, property: view.property, startDate: e.startDate, endDate: view.endDate,
          daysLeft: daysBetween(today, view.endDate), status: e.status, autoRenew: e.autoRenew, stepCount: e.steps.length,
          renewalStatus: renewal ? (renewal.status as types.RenewalStatus) : 'NONE', renewalId: renewal?.id ?? null, quoteTotal: money(quoteSteps.reduce((a, b) => a + b, 0)),
        };
      })
      .filter((r) => filters.daysUntilEnd === undefined || r.daysLeft <= filters.daysUntilEnd)
      .filter((r) => filters.hasRenewal === undefined || (r.renewalStatus !== 'NONE') === filters.hasRenewal)
      .sort((a, b) => a.endDate.localeCompare(b.endDate));
  }

  listRenewals(filters: types.RenewalListFilters = {}): types.RenewalPage {
    return this.paged(this.renewalRows(filters), filters.page, filters.limit);
  }

  exportRenewals(filters: types.RenewalListFilters = {}): types.CsvDownload {
    const header = ['Bundle', 'Customer', 'Property', 'Start date', 'End date', 'Days left', 'Auto-renew', 'Renewal status', 'Quote total'];
    const cell = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const lines = this.renewalRows({ ...filters, page: undefined, limit: undefined }).map((r) =>
      [r.bundleName, r.customer.name, r.property.address, r.startDate, r.endDate, String(r.daysLeft), r.autoRenew ? 'Yes' : 'No', r.renewalStatus, r.quoteTotal].map(cell).join(','));
    return { blob: new Blob([[header.map(cell).join(','), ...lines].join('\n')], { type: 'text/csv' }), filename: 'renewals.csv' };
  }

  approve(id: string, data: types.ApproveEnrollmentRequest = {}): types.BundleEnrollment {
    const user = this.requireOffice();
    const enrollment = this.liveEnrollment(user.tenantId, id);
    if (enrollment.status !== 'PENDING_APPROVAL') throw apiError(409, 'This enrollment is not waiting for approval', 'ENROLLMENT_NOT_PENDING');
    if (data.startDate !== undefined && !validDate(data.startDate)) throw apiError(400, 'startDate: Must be a date (YYYY-MM-DD)', 'VALIDATION_ERROR');
    const source = enrollment.renewedFromId ? this.enrollments.find((e) => e.id === enrollment.renewedFromId) : null;
    // The renewal is priced at the source's current effective renewal prices
    const current = enrollment.steps.map((s) => {
      const src = source?.steps.find((x) => x.step === s.step);
      return { step: s, price: src ? this.effectivePrice(source!, src).price : s.unitPrice };
    });
    const currentTotal = current.reduce((sum, c) => sum + Number(c.step.quantity) * Number(c.price), 0);
    const storedTotal = enrollment.steps.reduce((sum, s) => sum + Number(s.quantity) * Number(s.unitPrice), 0);
    const expected = data.expectedTotal !== undefined ? Number(data.expectedTotal) : storedTotal;
    if (Math.abs(expected - currentTotal) > 0.004 && !data.confirmPriceChange) {
      const details: types.RenewalPriceChangedDetails = {
        expectedTotal: money(expected), currentTotal: money(currentTotal),
        steps: current.filter((c) => Number(c.step.unitPrice) !== Number(c.price)).map((c) => ({ step: c.step.step, itemName: c.step.itemName, from: c.step.unitPrice, to: money(Number(c.price)) })),
      };
      throw apiError(409, 'The prices changed since this quote was shown; confirm the new total', 'RENEWAL_PRICE_CHANGED', details);
    }
    current.forEach((c) => { c.step.unitPrice = money(Number(c.price)); });
    if (data.startDate && data.startDate !== enrollment.startDate) {
      const dates = this.planDates(data.startDate, enrollment.steps.map((s) => s.daysAfterPrevious));
      enrollment.steps.forEach((s, index) => { s.plannedDate = dates[index]; });
      enrollment.startDate = data.startDate;
    }
    if (data.autoRenew !== undefined) enrollment.autoRenew = data.autoRenew;
    this.createVisitJobs(enrollment, data.assignedTo ?? null);
    enrollment.status = 'ACTIVE';
    enrollment.updatedAt = this.nowIso();
    return this.presentEnrollment(enrollment);
  }

  decline(id: string, reason?: string): types.BundleEnrollment {
    const user = this.requireOffice();
    const enrollment = this.liveEnrollment(user.tenantId, id);
    if (enrollment.status !== 'PENDING_APPROVAL') throw apiError(409, 'This enrollment is not waiting for approval', 'ENROLLMENT_NOT_PENDING');
    enrollment.status = 'CANCELLED';
    enrollment.cancelledAt = this.nowIso();
    enrollment.cancelReason = reason?.trim() || null;
    const source = enrollment.renewedFromId ? this.enrollments.find((e) => e.id === enrollment.renewedFromId) : null;
    if (source && source.renewedToId === enrollment.id) source.renewedToId = null;
    return this.presentEnrollment(enrollment);
  }

  // ------------------------------------------------------------ estimates (tracker item 85)

  /**
   * The visit template of a bundle for an estimate: every component with its offset from the FIRST component (the
   * program calendar, skipped components included) and whether its item can still be scheduled. Null = not found.
   */
  bundleTemplate(tenantId: string, bundleId: string): MockBundleTemplate | null {
    const bundle = this.bundles.find((b) => b.id === bundleId && b.tenantId === tenantId && !b.deletedAt);
    if (!bundle) return null;
    let offset = 0;
    return {
      id: bundle.id, name: bundle.name, isActive: bundle.isActive, autoRenewDefault: bundle.autoRenewDefault, description: bundle.description,
      components: bundle.components.map((c, index) => {
        if (index > 0) offset += c.daysAfterPrevious;
        const item = this.host.item(tenantId, c.itemId);
        const available = !!item && !item.deletedAt && item.isActive !== false;
        return {
          id: c.id, sortOrder: index + 1, itemId: c.itemId, itemName: item?.name ?? 'Deleted item', quantity: c.quantity, offsetDays: offset,
          daysAfterPrevious: index === 0 ? 0 : c.daysAfterPrevious, notes: c.notes, available, reason: !item || item.deletedAt ? 'ITEM_DELETED' : item.isActive === false ? 'ITEM_INACTIVE' : null,
        };
      }),
    };
  }

  /**
   * Converting an estimate: one ACTIVE enrollment from the remaining bundle lines at the quoted prices. The first remaining
   * visit falls on `startDate`, the others keep their gaps (offset differences). A visit item deleted since fails the whole
   * conversion (422 ENROLLMENT_ITEM_UNAVAILABLE).
   */
  createFromEstimate(tenantId: string, input: {
    propertyId: string; bundleId: string | null; bundleName: string; startDate: string; assignedTo: string | null;
    lines: Array<{ itemId: string; quantity: string; unitPrice: string; offsetDays: number }>;
  }): { id: string } {
    const user = this.user();
    const property = this.host.property(tenantId, input.propertyId);
    if (!property) throw apiError(404, 'Property not found', 'NOT_FOUND');
    const lines = [...input.lines].sort((a, b) => a.offsetDays - b.offsetDays);
    lines.forEach((line) => {
      const item = this.host.item(tenantId, line.itemId);
      if (!item || item.deletedAt) throw apiError(422, 'A visit item is no longer available', 'ENROLLMENT_ITEM_UNAVAILABLE', { itemId: line.itemId });
    });
    const bundle = input.bundleId ? this.bundles.find((b) => b.id === input.bundleId && b.tenantId === tenantId && !b.deletedAt) : null;
    const now = this.nowIso();
    const first = lines[0]?.offsetDays ?? 0;
    const enrollment: Enrollment = {
      id: this.id('enr'), tenantId, bundleId: bundle?.id ?? null, bundleName: input.bundleName, customerId: property.customerId, propertyId: property.id,
      startDate: input.startDate, status: 'ACTIVE', autoRenew: bundle?.autoRenewDefault ?? false, renewedFromId: null, renewedToId: null,
      createdBy: user.id, cancelledAt: null, cancelReason: null, steps: [], createdAt: now, updatedAt: now,
    };
    enrollment.steps = lines.map((line, index): Step => {
      const item = this.host.item(tenantId, line.itemId)!;
      return {
        step: index + 1, itemId: line.itemId, itemName: item.name, quantity: line.quantity, daysAfterPrevious: index === 0 ? 0 : line.offsetDays - lines[index - 1].offsetDays,
        plannedDate: addDays(input.startDate, line.offsetDays - first), unitPrice: money(Number(line.unitPrice)), renewalUnitPrice: null, jobId: null,
      };
    });
    this.createVisitJobs(enrollment, input.assignedTo);
    this.enrollments.push(enrollment);
    return { id: enrollment.id };
  }

  // ------------------------------------------------------------ intake queue (staff Requests inbox)

  /** GET /enrollments/pending: PENDING_APPROVAL enrollments (portal requests and renewals), oldest first. */
  listPending(filters: types.PendingEnrollmentFilters = {}): types.PendingEnrollmentPage {
    const user = this.requireOffice();
    if (filters.type !== undefined && filters.type !== 'BUNDLE_REQUEST' && filters.type !== 'BUNDLE_RENEWAL') throw apiError(400, 'type: Must be BUNDLE_REQUEST or BUNDLE_RENEWAL', 'VALIDATION_ERROR');
    const rows = this.enrollments
      .filter((e) => e.tenantId === user.tenantId && e.status === 'PENDING_APPROVAL')
      .map((e): types.PendingEnrollmentEntry => {
        const view = this.presentEnrollment(e);
        const quote = this.getQuote(e.id);
        return {
          type: e.renewedFromId ? 'BUNDLE_RENEWAL' : 'BUNDLE_REQUEST', id: e.id, link: `/enrollments/${e.id}`, bundleName: e.bundleName, customer: view.customer,
          property: view.property, startDate: e.startDate, endDate: view.endDate, stepCount: e.steps.length, autoRenew: e.autoRenew, total: quote.subtotal,
          renewedFromId: e.renewedFromId, createdBy: e.createdBy, createdAt: e.createdAt,
        };
      })
      .filter((entry) => !filters.type || entry.type === filters.type)
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    return this.paged(rows, filters.page, filters.limit);
  }

  // ------------------------------------------------------------ customer portal

  private requireLinkedCustomer(): { tenantId: string; userId: string; email: string; customerId: string } {
    const scope = this.host.portal();
    if (!scope.customerId) throw apiError(403, 'Your account is not linked to a customer yet', 'PORTAL_NOT_LINKED');
    return { ...scope, customerId: scope.customerId };
  }

  /** GET /portal/requestable-bundles ?propertyId&startDate */
  requestableBundles(query: { propertyId?: string; startDate?: string } = {}): types.PortalRequestableBundle[] {
    const scope = this.host.portal();
    const own = scope.customerId ? this.host.ownProperties(scope.tenantId, scope.customerId) : [];
    let priced: MockBundleProperty | null = own[0] ?? null;
    if (query.propertyId) {
      priced = own.find((p) => p.id === query.propertyId) ?? null;
      if (!priced) throw apiError(404, 'Property not found', 'NOT_FOUND');
    }
    if (query.startDate && !validDate(query.startDate)) throw apiError(400, 'startDate must be a date (YYYY-MM-DD)', 'VALIDATION_ERROR');
    return this.bundles
      .filter((b) => b.tenantId === scope.tenantId && !b.deletedAt && b.isActive && b.portalRequestable)
      .sort((a, b) => a.name.localeCompare(b.name))
      .flatMap((bundle): types.PortalRequestableBundle[] => {
        const template = this.bundleTemplate(scope.tenantId, bundle.id)!;
        const usable = template.components.filter((c) => c.available);
        if (usable.length === 0) return [];
        let prices: Map<number, number> | null = null;
        if (priced) {
          try {
            prices = new Map(usable.map((c, index) => [index + 1, Number(c.quantity) * Number(this.host.priceFor(scope.tenantId, priced!.id, c.itemId).unitPrice)]));
          } catch {
            prices = null; // a price that cannot be computed leaves this bundle unpriced
          }
        }
        const subtotal = prices ? [...prices.values()].reduce((a, b) => a + b, 0) : null;
        return [{
          id: bundle.id, name: bundle.name, description: bundle.description, autoRenewDefault: bundle.autoRenewDefault, visitCount: usable.length,
          visitSpanDays: usable[usable.length - 1].offsetDays,
          visits: usable.map((c, index) => ({
            step: index + 1, itemId: c.itemId, itemName: c.itemName, quantity: c.quantity, offsetDays: c.offsetDays, daysAfterPrevious: index === 0 ? 0 : c.daysAfterPrevious,
            estimatedPrice: prices ? money(prices.get(index + 1)!) : null,
          })),
          pricedForPropertyId: prices ? priced!.id : null,
          estimatedSubtotal: subtotal === null ? null : money(subtotal), estimatedTax: subtotal === null ? null : money(0), estimatedTotal: subtotal === null ? null : money(subtotal),
        }];
      });
  }

  /** POST /portal/bundle-requests: a PENDING_APPROVAL enrollment, no jobs. */
  requestBundle(body: types.PortalBundleRequestInput): types.PortalEnrollment {
    const scope = this.requireLinkedCustomer();
    if (!body || typeof body.bundleId !== 'string' || !body.bundleId.trim()) throw apiError(400, 'bundleId is required', 'VALIDATION_ERROR');
    if (typeof body.propertyId !== 'string' || !body.propertyId.trim()) throw apiError(400, 'propertyId is required', 'VALIDATION_ERROR');
    if (!validDate(body.startDate)) throw apiError(400, 'startDate must be a date (YYYY-MM-DD)', 'VALIDATION_ERROR');
    if (body.autoRenew !== undefined && typeof body.autoRenew !== 'boolean') throw apiError(400, 'autoRenew must be true or false', 'VALIDATION_ERROR');
    if (body.startDate < this.host.today()) throw apiError(400, 'startDate cannot be in the past', 'VALIDATION_ERROR');
    const bundle = this.bundles.find((b) => b.id === body.bundleId && b.tenantId === scope.tenantId && !b.deletedAt && b.isActive && b.portalRequestable);
    if (!bundle) throw apiError(404, 'Service bundle not found', 'NOT_FOUND');
    if (!this.host.ownProperties(scope.tenantId, scope.customerId).some((p) => p.id === body.propertyId)) throw apiError(404, 'Property not found', 'NOT_FOUND');
    const open = this.enrollments.filter((e) => e.tenantId === scope.tenantId && e.customerId === scope.customerId && e.status === 'PENDING_APPROVAL' && !e.renewedFromId);
    if (open.length >= 20) throw apiError(409, 'You have too many open requests; please wait for a reply', 'TOO_MANY_OPEN_REQUESTS');
    if (open.some((e) => e.bundleId === bundle.id && e.propertyId === body.propertyId)) {
      throw apiError(409, 'You already requested this program for this property; we will be in touch', 'BUNDLE_REQUEST_EXISTS');
    }
    const { preview } = this.plan(scope.tenantId, bundle, body.propertyId, body.startDate);
    const now = this.nowIso();
    const enrollment: Enrollment = {
      id: this.id('enr'), tenantId: scope.tenantId, bundleId: bundle.id, bundleName: bundle.name, customerId: scope.customerId, propertyId: body.propertyId,
      startDate: body.startDate, status: 'PENDING_APPROVAL', autoRenew: body.autoRenew ?? bundle.autoRenewDefault, renewedFromId: null, renewedToId: null,
      createdBy: scope.userId, cancelledAt: null, cancelReason: null, createdAt: now, updatedAt: now,
      steps: preview.visits.map((v): Step => ({
        step: v.step, itemId: v.itemId, itemName: v.itemName, quantity: v.quantity, daysAfterPrevious: v.daysAfterPrevious, plannedDate: v.plannedDate,
        unitPrice: v.unitPrice, renewalUnitPrice: null, jobId: null,
      })),
    };
    this.enrollments.push(enrollment);
    this.alert(enrollment, 'BUNDLE_REQUEST_PENDING', 'Bundle request awaiting approval', `${bundle.name} was requested through the customer portal.`, scope.userId);
    return this.toPortal(this.presentEnrollment(enrollment));
  }

  /** The customer sees their own enrollments, never a renewal staff created that is not approved (or was declined). */
  private customerVisible(e: Enrollment): boolean {
    if (!e.renewedFromId) return true;
    if (e.status === 'PENDING_APPROVAL') return false;
    if (e.status === 'CANCELLED' && !e.steps.some((s) => s.jobId)) return false;
    return true;
  }

  private portalEnrollment(scope: { tenantId: string; customerId: string }, id: string): Enrollment {
    const found = this.enrollments.find((e) => e.id === id && e.tenantId === scope.tenantId && e.customerId === scope.customerId && this.customerVisible(e));
    if (!found) throw apiError(404, 'Enrollment not found', 'NOT_FOUND');
    return found;
  }

  private visitStatus(step: types.EnrollmentStep, status: types.EnrollmentStatus): types.PortalVisitStatus {
    if (!step.job) return status === 'PENDING_APPROVAL' ? 'REQUESTED' : status === 'CANCELLED' ? 'CANCELLED' : 'PLANNED';
    if (DONE_JOB_STATUSES.includes(step.job.status)) return 'COMPLETED';
    if (step.job.status === 'IN_PROGRESS') return 'IN_PROGRESS';
    if (step.job.status === 'CANCELLED') return 'CANCELLED';
    return 'SCHEDULED';
  }

  /** What the customer sees: no technician, cost, invoice or renewal-price data. */
  private toPortal(e: types.BundleEnrollment): types.PortalEnrollment {
    return {
      id: e.id, bundleName: e.bundleName, property: e.property, startDate: e.startDate, endDate: e.endDate, status: e.status, autoRenew: e.autoRenew,
      stepCount: e.stepCount, progress: e.progress, nextVisitDate: e.nextVisitDate, total: e.total, createdAt: e.createdAt,
      steps: e.steps.map((s): types.PortalEnrollmentStep => ({
        step: s.step, itemName: s.itemName, quantity: s.quantity, plannedDate: s.plannedDate, scheduledDate: s.job?.scheduledDate ?? s.plannedDate,
        completedDate: s.job?.completedDate ?? null, status: this.visitStatus(s, e.status), unitPrice: s.jobUnitPrice ?? s.unitPrice, lineTotal: s.lineTotal, jobId: s.jobId,
      })),
    };
  }

  listPortalEnrollments(query: types.PortalEnrollmentListQuery = {}): types.PortalEnrollmentPage {
    const scope = this.requireLinkedCustomer();
    const rows = this.enrollments
      .filter((e) => e.tenantId === scope.tenantId && e.customerId === scope.customerId && this.customerVisible(e))
      .map((e) => this.presentEnrollment(e))
      .filter((e) => !query.status || e.status === query.status)
      .sort((a, b) => b.startDate.localeCompare(a.startDate))
      .map((e) => this.toPortal(e));
    return this.paged(rows, query.page, query.limit);
  }

  getPortalEnrollment(id: string): types.PortalEnrollment {
    const scope = this.requireLinkedCustomer();
    return this.toPortal(this.presentEnrollment(this.portalEnrollment(scope, id)));
  }

  /** PATCH /portal/enrollments/:id { autoRenew }: the customer's switch. It never approves anything. */
  setPortalAutoRenew(id: string, autoRenew: boolean): types.PortalEnrollment {
    const scope = this.requireLinkedCustomer();
    if (typeof autoRenew !== 'boolean') throw apiError(400, 'autoRenew must be true or false', 'VALIDATION_ERROR');
    const enrollment = this.portalEnrollment(scope, id);
    if (enrollment.status === 'CANCELLED') throw apiError(409, 'A cancelled enrollment cannot be changed', 'ENROLLMENT_CANCELLED');
    enrollment.autoRenew = autoRenew;
    enrollment.updatedAt = this.nowIso();
    return this.toPortal(this.presentEnrollment(enrollment));
  }

  // ------------------------------------------------------------ alert routing

  private defaultRule(): types.BundleAlertRule {
    return { roles: ['ADMIN', 'OFFICE_MANAGER'], userIds: [], everyone: false, enrollmentCreator: false };
  }

  private routingFor(tenantId: string): types.BundleAlertRouting {
    return this.routing.get(tenantId) ?? { BUNDLE_RENEWAL_PENDING: this.defaultRule(), BUNDLE_REQUEST_PENDING: this.defaultRule(), BUNDLE_RENEWAL_BLOCKED: this.defaultRule() };
  }

  private eligible(tenantId: string): MockBundleUser[] {
    return this.host.users(tenantId).filter((u) => u.isActive && OFFICE.includes(u.role));
  }

  /** The users a rule resolves to; nobody falls back to the ADMIN users. */
  private recipients(tenantId: string, rule: types.BundleAlertRule, creatorId: string | null): MockBundleUser[] {
    const pool = this.eligible(tenantId);
    const ids = new Set<string>();
    pool.filter((u) => rule.everyone || rule.roles.includes(u.role as types.BundleAlertRole) || rule.userIds.includes(u.id)).forEach((u) => ids.add(u.id));
    if (rule.enrollmentCreator && creatorId && pool.some((u) => u.id === creatorId)) ids.add(creatorId);
    const chosen = pool.filter((u) => ids.has(u.id));
    return chosen.length ? chosen : pool.filter((u) => u.role === 'ADMIN');
  }

  private alert(enrollment: Enrollment, type: types.BundleAlertType, title: string, body: string, creatorId: string | null = enrollment.createdBy): void {
    const rule = this.routingFor(enrollment.tenantId)[type];
    const userIds = this.recipients(enrollment.tenantId, rule, creatorId).map((u) => u.id);
    this.host.alert?.(enrollment.tenantId, type, { title, body, enrollmentId: enrollment.id, userIds });
  }

  private routingResponse(tenantId: string): types.BundleAlertRoutingResponse {
    const routing = this.routingFor(tenantId);
    const pool = this.eligible(tenantId);
    const warnings: types.BundleAlertWarning[] = [];
    ALERT_TYPES.forEach((type) => {
      const rule = routing[type];
      const live = pool.filter((u) => rule.everyone || rule.roles.includes(u.role as types.BundleAlertRole) || rule.userIds.includes(u.id));
      if (live.length === 0 && !rule.enrollmentCreator) {
        warnings.push({ type, code: 'NO_ACTIVE_RECIPIENTS', message: 'Nobody is chosen to receive this alert, so it goes to all admins.' });
      }
    });
    return {
      routing, warnings,
      eligibleUsers: pool.map((u) => ({ id: u.id, email: u.email, firstName: u.firstName ?? null, lastName: u.lastName ?? null, role: u.role })),
    };
  }

  getRouting(): types.BundleAlertRoutingResponse {
    const user = this.user();
    if (user.role !== 'ADMIN') throw apiError(403, 'Insufficient permissions', 'INSUFFICIENT_PERMISSIONS');
    return this.routingResponse(user.tenantId);
  }

  updateRouting(input: Partial<types.BundleAlertRouting>): types.BundleAlertRoutingResponse {
    const user = this.user();
    if (user.role !== 'ADMIN') throw apiError(403, 'Insufficient permissions', 'INSUFFICIENT_PERMISSIONS');
    const pool = this.eligible(user.tenantId);
    const next = {} as types.BundleAlertRouting;
    ALERT_TYPES.forEach((type) => {
      const rule = input?.[type];
      if (rule === undefined) { next[type] = this.defaultRule(); return; }
      const roles = Array.isArray(rule.roles) ? rule.roles : [];
      if (roles.some((r) => !OFFICE.includes(r))) throw apiError(400, `${type}: Only ADMIN and OFFICE_MANAGER can receive bundle alerts`, 'VALIDATION_ERROR');
      const userIds = Array.isArray(rule.userIds) ? rule.userIds : [];
      if (userIds.some((id) => !pool.some((u) => u.id === id))) throw apiError(400, `${type}: Choose active admin or office manager users of this company`, 'VALIDATION_ERROR');
      next[type] = { roles: [...new Set(roles)], userIds: [...new Set(userIds)], everyone: !!rule.everyone, enrollmentCreator: !!rule.enrollmentCreator };
    });
    this.routing.set(user.tenantId, next);
    return this.routingResponse(user.tenantId);
  }

  /** Test helper: the ids an alert of this type would reach for the enrollment. */
  recipientIdsFor(tenantId: string, type: types.BundleAlertType, creatorId: string | null): string[] {
    return this.recipients(tenantId, this.routingFor(tenantId)[type], creatorId).map((u) => u.id);
  }
}
